<template>
  <div class="billing-status-wrapper">
    <!-- Show statement badges if billed -->
    <div 
      v-if="hasBilledInvoices"
      class="d-flex justify-content-center flex-wrap gap-1"
    >
      <div
        v-for="(invoice, index) in billedInvoices"
        :key="index"
        class="badge-billing-info"
      >
        {{ getStatementNumber(invoice) }}
      </div>
    </div>
    
    <!-- Show toggle switch if not billed -->
    <div 
      v-else
      @click="onToggleClick"
    >
      <switches
        :value="isIncluded"
        type-bold="true"
        theme="bootstrap"
        color="success"
        text-enabled="Included"
        text-disabled="Excluded"
        :disabled="disabled"
      />
    </div>
  </div>
</template>

<script>
// Renders the billing state for a reading: when the reading is on one or more statements
// (AssociatedInvoices) it shows the statement-number pills; otherwise it shows the legacy
// Included/Excluded toggle (vue-switches, registered globally as `switches` in main.js — the
// same component the legacy GasReadings/ElectricityReadingsNHH used, which supports the
// `text-enabled`/`text-disabled` labels). We deliberately do NOT register a local `switches`
// component here so the `<switches>` tag resolves to the global vue-switches.
export default {
  name: 'BillingStatusToggle',

  props: {
    value: {
      type: [Array, Boolean, Object],
      default: null
    },
    row: {
      type: Object,
      required: true
    },
    field: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Field name for associated invoices
    invoicesField: {
      type: String,
      default: 'associatedInvoices'
    },
    // Field name for billing exclusion flag
    excludeField: {
      type: String,
      default: 'doNotBill'
    },
    // Function to get billing info (matches your existing pattern)
    billingInfoResolver: {
      type: Function,
      default: null
    }
  },

  computed: {
    associatedInvoices() {
      return this.row[this.invoicesField] || []
    },

    hasBilledInvoices() {
      return this.associatedInvoices && this.associatedInvoices.length > 0
    },

    billedInvoices() {
      return this.hasBilledInvoices ? this.associatedInvoices : []
    },

    isIncluded() {
      // If there's a specific value prop (for boolean field implementation)
      if (typeof this.value === 'boolean') {
        return this.value
      }
      
      // Otherwise check the exclude field (inverted logic)
      return !this.row[this.excludeField]
    }
  },

  methods: {
    getStatementNumber(invoice) {
      // Use the provided billing info resolver if available
      if (this.billingInfoResolver && typeof this.billingInfoResolver === 'function') {
        const billingInfo = this.billingInfoResolver(invoice)
        return billingInfo && billingInfo.length > 0 
          ? billingInfo[0].StatementNumber 
          : ''
      }
      
      // Fallback to direct property access
      return invoice.StatementNumber || 
             invoice.statementNumber || 
             (invoice.billingInfo && invoice.billingInfo.StatementNumber) ||
             ''
    },

    onToggleClick(event) {
      // Prevent row click events from bubbling up
      event.stopPropagation()
      
      // Emit the toggle event with the current row and new state
      this.$emit('billing-toggle', {
        row: this.row,
        currentValue: this.isIncluded,
        newValue: !this.isIncluded,
        field: this.excludeField
      })
    }
  }
}
</script>

<style scoped>
.billing-status-wrapper {
    min-height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.badge-billing-info {
    border: 1px solid #28a745;
    color: #28a745;
    margin: .5em;
    min-width: 2em;
    border-radius: .2em;
}

.gap-1 > * + * {
  margin-left: 0.25rem;
}
</style>